import React, { useState, useMemo } from 'react';
import { Calendar, Clock, Tag, Edit2, Trash2, MessageSquare, Sparkles, Bot, SortAsc, Moon, Sun } from 'lucide-react';
import { JournalEntry } from '../../types';

interface JournalBookProps {
  entries: JournalEntry[];
  onEdit: (entry: JournalEntry) => void;
  onDelete: (id: string) => void;
}

type SortOption = 'date-desc' | 'date-asc' | 'shadow-work' | 'light-work' | 'template';

export default function JournalBook({ entries, onEdit, onDelete }: JournalBookProps) {
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  // Format date nicely
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  // Format time nicely
  const formatTime = (date: string) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get template info
  const getTemplateInfo = (entry: JournalEntry) => {
    if (entry.entry_type === 'chat_summary') {
      return {
        icon: <Bot className="h-4 w-4" />,
        label: 'Chat Summary',
        color: 'bg-amber-100 text-amber-800'
      };
    }
    return {
      icon: <Sparkles className="h-4 w-4" />,
      label: entry.template_type?.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ') || 'Quick Entry',
      color: 'bg-mint-100 text-mint-800'
    };
  };

  // Determine if entry is shadow work or light work
  const isLightWork = (entry: JournalEntry) => {
    const lightWorkTemplates = ['gratitude', 'affirmation', 'manifestation', 'meditation', 'integration'];
    return lightWorkTemplates.includes(entry.template_type || '');
  };

  // Sort entries based on selected option
  const sortedEntries = useMemo(() => {
    let sorted = [...entries];
    
    switch (sortBy) {
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'shadow-work':
        return sorted.sort((a, b) => {
          const aIsShadow = !isLightWork(a);
          const bIsShadow = !isLightWork(b);
          return bIsShadow ? 1 : aIsShadow ? -1 : 0;
        });
      case 'light-work':
        return sorted.sort((a, b) => {
          const aIsLight = isLightWork(a);
          const bIsLight = isLightWork(b);
          return bIsLight ? 1 : aIsLight ? -1 : 0;
        });
      case 'template':
        return sorted.sort((a, b) => {
          const aTemplate = a.template_type || 'quick';
          const bTemplate = b.template_type || 'quick';
          return aTemplate.localeCompare(bTemplate);
        });
      default:
        return sorted;
    }
  }, [entries, sortBy]);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-mint-200 overflow-hidden">
      {/* Sort Controls */}
      <div className="p-4 border-b border-mint-200 bg-mint-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SortAsc className="h-5 w-5 text-midnight-600" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-3 py-1.5 rounded-lg border border-mint-200 text-midnight-700 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="shadow-work">Shadow Work First</option>
              <option value="light-work">Light Work First</option>
              <option value="template">By Template</option>
            </select>
          </div>
          
          {/* Work Type Indicator */}
          {(sortBy === 'shadow-work' || sortBy === 'light-work') && (
            <div className="flex items-center space-x-2 text-sm">
              {sortBy === 'shadow-work' ? (
                <>
                  <Moon className="h-4 w-4 text-midnight-600" />
                  <span className="text-midnight-600">Shadow Work</span>
                </>
              ) : (
                <>
                  <Sun className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-600">Light Work</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="divide-y divide-mint-100">
        {sortedEntries.length > 0 ? (
          sortedEntries.map(entry => (
            <div key={entry.id} className="group">
              {/* Entry Header */}
              <div 
                onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                className="p-4 hover:bg-mint-50/80 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-midnight-800">{entry.title}</h3>
                  <div className="flex items-center space-x-2">
                    {/* Template Badge */}
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getTemplateInfo(entry).color}`}>
                      {getTemplateInfo(entry).icon}
                      <span>{getTemplateInfo(entry).label}</span>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex flex-wrap gap-4 text-sm text-midnight-500">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(entry.created_at)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>{formatTime(entry.created_at)}</span>
                  </div>
                </div>

                {/* Preview */}
                {!expandedEntry && (
                  <p className="mt-2 text-midnight-600 line-clamp-2">
                    {entry.content}
                  </p>
                )}

                {/* Tags */}
                {entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {entry.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center space-x-1 px-2 py-1 bg-mint-50 text-midnight-600 rounded-full text-xs"
                      >
                        <Tag className="h-3 w-3" />
                        <span>{tag}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Expanded Content */}
              {expandedEntry === entry.id && (
                <div className="p-6 bg-white border-t border-mint-100">
                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-2 mb-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(entry);
                      }}
                      className="p-2 hover:bg-mint-100 rounded-lg text-midnight-600 transition-colors"
                      title="Edit entry"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(entry.id);
                      }}
                      className="p-2 hover:bg-bubblegum-100 rounded-lg text-midnight-600 transition-colors"
                      title="Delete entry"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>

                    {/* Delete Confirmation */}
                    {showDeleteConfirm === entry.id && (
                      <div className="absolute right-6 mt-12 bg-white rounded-lg shadow-lg border border-bubblegum-200 p-4 z-10">
                        <p className="text-midnight-600 mb-4">Delete this entry?</p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteConfirm(null);
                            }}
                            className="px-3 py-1.5 text-midnight-600 hover:text-midnight-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(entry.id);
                              setShowDeleteConfirm(null);
                            }}
                            className="px-3 py-1.5 bg-bubblegum-500 text-white rounded-lg hover:bg-bubblegum-600"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Entry Content */}
                  <div className="prose prose-lg prose-mint max-w-none">
                    <div className="whitespace-pre-wrap">
                      {entry.content}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="p-12 flex flex-col items-center justify-center text-midnight-500">
            <MessageSquare className="h-12 w-12 mb-4" />
            <p>No journal entries yet</p>
          </div>
        )}
      </div>
    </div>
  );
}